import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import {
  FiArrowDown,
  FiArrowUp,
  FiBarChart2,
  FiBox,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiPieChart,
  FiTruck,
  FiUser,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import BookingDetails from "../Components/BookingDetails";
import {
  getDashboard,
  getPartnerDashboard,
} from "../Services/dashboardService";
import { logisticTypeColors, RoutesPaths } from "../Router/constant";
import { getBookings, getPartnerBookings } from "../Services/bookingService";
import { getAdminProfile, getUsersSubscribers } from "../Services/userService";

Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController,
  DoughnutController,
  ArcElement,
);

const ALL_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const BAR_SERIES = [
  { key: "pendingBookings", label: "Pending", color: "#FF9F1C" },
  { key: "inprogressBookings", label: "In-progress", color: "#387CF3" },
  { key: "transitBookings", label: "In-transit", color: "#7C5CE0" },
  { key: "deliveredBookings", label: "Delivered", color: "#4CAF50" },
  { key: "totalBookings", label: "Total", color: "#F04927" },
];

const RANGE_OPTIONS = [
  { value: 12, label: "Full year" },
  { value: 6, label: "Last 6 months" },
  { value: 3, label: "Last 3 months" },
];

const EMPTY_COLOR = "#D5D9E0";

const formatLogisticsType = (type = "") =>
  type
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^./, (char) => char.toUpperCase());

// Re-maps an API series onto all 12 months so missing months render as 0.
const toYearSeries = (labels = [], values = []) => {
  const byMonth = {};
  labels.forEach((month, i) => {
    byMonth[month] = Number(values?.[i]) || 0;
  });
  return ALL_MONTHS.map((month) => byMonth[month] || 0);
};

const getTrend = (series = []) => {
  const monthIndex = new Date().getMonth();
  const current = series[monthIndex] || 0;
  const previous = series[monthIndex - 1] || 0;
  if (!previous) return current > 0 ? { value: 100, up: true } : null;
  const change = Math.round(((current - previous) / previous) * 100);
  return { value: Math.abs(change), up: change >= 0 };
};

function Sparkline({ data, color }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !data?.length) return;

    const chart = new Chart(canvasRef.current, {
      type: "line",
      data: {
        labels: data.map((_, i) => i),
        datasets: [
          {
            data,
            borderColor: color,
            borderWidth: 2,
            tension: 0.4,
            pointRadius: 0,
            fill: true,
            backgroundColor: (ctx) => {
              const { chartArea, ctx: c } = ctx.chart;
              if (!chartArea) return "transparent";
              const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              g.addColorStop(0, `${color}55`);
              g.addColorStop(1, `${color}00`);
              return g;
            },
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false, beginAtZero: true } },
      },
    });

    return () => chart.destroy();
  }, [data, color]);

  return (
    <div className="qd-sparkline">
      <canvas ref={canvasRef} />
    </div>
  );
}

function StatCard({ icon, tone, label, value, loading, trend, spark, caption, onClick }) {
  return (
    <div className={`qd-card qd-stat ${onClick ? "qd-clickable" : ""}`} onClick={onClick}>
      <div className={`qd-stat-icon qd-tone-${tone}`}>{icon}</div>
      <div className="qd-stat-body">
        <p className="qd-stat-label">{label}</p>
        {/* value and sparkline share a row so the chart never overlaps the text below */}
        <div className="qd-stat-value-row">
          {loading ? (
            <div className="qd-skel" style={{ width: 60, height: 30 }} />
          ) : (
            <h3 className="qd-stat-value">{Number(value || 0).toLocaleString()}</h3>
          )}
          {/* {spark && <Sparkline data={spark.data} color={spark.color} />} */}
        </div>
        <div className="qd-stat-meta">
          {trend ? (
            <>
              <span className={`qd-trend ${trend.up ? "up" : "down"}`}>
                {trend.up ? <FiArrowUp /> : <FiArrowDown />} {trend.value}%
              </span>
              <span>from last month</span>
            </>
          ) : (
            <span>{caption}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState({});
  const [bookings, setBookings] = useState([]);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState("");
  const [statsRange, setStatsRange] = useState(12);
  const barCanvasRef = useRef(null);
  const donutCanvasRef = useRef(null);
  const navigate = useNavigate();

  const isAdmin = userRole === "admin";

  useEffect(() => {
    let timer;
    let tries = 0;

    const syncRole = () => {
      const role = localStorage.getItem("userRole");
      if (role) return setUserRole(role);

      if (tries++ < 15) timer = setTimeout(syncRole, 200);
    };

    syncRole();

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getAdminProfile();
        setUserData(res?.data?.data?.user);
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    };
    fetchProfile();
  }, []);

  const fetchBookings = async () => {
    setLoadingBookings(true);

    try {
      const response = isAdmin
        ? await getBookings()
        : await getPartnerBookings();

      if (response?.data?.status === true) {
        setBookings(response.data.data.orders || []);
      } else {
        setBookings([]);
      }
    } catch (err) {
      setBookings([]);
      console.error(err);
    } finally {
      setLoadingBookings(false);
    }
  };

  const fetchAllData = async () => {
    setLoadingDashboard(true);
    try {
      const dashboardResp = isAdmin
        ? await getDashboard()
        : await getPartnerDashboard();

      setDashboardData(dashboardResp?.data?.data || {});
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoadingDashboard(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const resp = await getUsersSubscribers({ offset: 1, limit: 1 });
      setSubscriberCount(resp?.data?.data?.count || 0);
    } catch (error) {
      console.error("Error fetching subscribers:", error);
    }
  };

  useEffect(() => {
    if (!userRole) return;

    fetchAllData();
    fetchBookings();
    if (userRole === "admin") fetchSubscribers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  // All monthly series normalised to Jan..Dec.
  const yearSeries = useMemo(() => {
    const monthly = dashboardData?.monthlyData || {};
    return BAR_SERIES.reduce((acc, s) => {
      acc[s.key] = toYearSeries(monthly.labels, monthly[s.key]);
      return acc;
    }, {});
  }, [dashboardData]);

  const statusCount = (...statuses) =>
    (dashboardData?.statusBreakdown || [])
      .filter((item) => statuses.includes(item.status?.toLowerCase()))
      .reduce((sum, item) => sum + (Number(item.count) || 0), 0);

  const activeShipments = statusCount("inprogress", "in progress", "in transit", "in-transit");
  const completedBookings = statusCount("completed");

  const activeSeries = useMemo(
    () =>
      ALL_MONTHS.map(
        (_, i) =>
          (yearSeries.inprogressBookings?.[i] || 0) +
          (yearSeries.transitBookings?.[i] || 0),
      ),
    [yearSeries],
  );

  const sparkWindow = (series = []) => {
    const end = new Date().getMonth() + 1;
    return series.slice(Math.max(0, end - 6), end);
  };

  const logistics = dashboardData?.logisticsBreakdown || [];
  const logisticsTotal = logistics.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
  const typeColor = (item) =>
    item.count > 0 ? logisticTypeColors[item.logisticsType] || EMPTY_COLOR : EMPTY_COLOR;

  // Booking stats bar chart
  useEffect(() => {
    if (!barCanvasRef.current || !dashboardData?.monthlyData) return;

    const end = statsRange === 12 ? 12 : new Date().getMonth() + 1;
    const start = Math.max(0, end - statsRange);

    const chart = new Chart(barCanvasRef.current, {
      type: "bar",
      data: {
        labels: ALL_MONTHS.slice(start, end),
        datasets: BAR_SERIES.map((s) => ({
          label: s.label,
          data: yearSeries[s.key].slice(start, end),
          backgroundColor: s.color,
          borderRadius: 3,
          barPercentage: 0.75,
          categoryPercentage: 0.6,
          maxBarThickness: 10,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { color: "#8A8F99", precision: 0, font: { size: 11 } },
            grid: { color: "#EEF0F3" },
            border: { display: false },
          },
          x: {
            ticks: { color: "#8A8F99", font: { size: 11 } },
            grid: { color: "#F4F5F7" },
            border: { display: false },
          },
        },
        plugins: {
          legend: {
            position: "top",
            labels: {
              boxWidth: 14,
              boxHeight: 10,
              useBorderRadius: true,
              borderRadius: 2,
              color: "#4B5160",
              font: { size: 12 },
              padding: 16,
            },
          },
          tooltip: {
            backgroundColor: "#1F2430",
            padding: 10,
            cornerRadius: 8,
          },
        },
      },
    });

    return () => chart.destroy();
  }, [dashboardData, yearSeries, statsRange, loadingDashboard]);

  // Logistics types donut
  useEffect(() => {
    if (!donutCanvasRef.current || !logistics.length) return;

    const hasData = logisticsTotal > 0;

    const chart = new Chart(donutCanvasRef.current, {
      type: "doughnut",
      data: {
        labels: hasData ? logistics.map((i) => formatLogisticsType(i.logisticsType)) : ["No data"],
        datasets: [
          {
            data: hasData ? logistics.map((i) => i.count) : [1],
            backgroundColor: hasData ? logistics.map(typeColor) : [EMPTY_COLOR],
            borderWidth: 0,
            hoverOffset: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "68%",
        plugins: {
          legend: { display: false },
          tooltip: { enabled: hasData },
        },
      },
    });

    return () => chart.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData, loadingDashboard]);

  const bookingsSpark = sparkWindow(yearSeries.totalBookings);
  const reviews = dashboardData?.reviews || [];
  const review = reviews[currentReviewIndex];

  return (
    <div className="container-fluid qd-page qd-dashboard">
      {/* Page header */}
      <div className="qd-page-header">
        <div>
          <p className="qd-welcome">Welcome back 👋</p>
          <h1 className="qd-title">Dashboard</h1>
          <p className="qd-subtitle">
            {userData?.fullName ? `Hi ${userData.fullName}, here's` : "Here's"} an
            overview of your logistics operations and bookings.
          </p>
        </div>
        <div className="qd-date-chip">
          {new Date().toLocaleDateString("en-GB", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </div>
      </div>

      {/* Partner contract banner */}
      {userRole === "partner" &&
        userData?.partnershipContract?.status === "pending" && (
          <div className="qd-card qd-contract">
            <div>
              <h5 className="qd-contract-title">
                <img src="/assets/img/featuredIcon.png" alt="" />
                Contract from Quimera team
              </h5>
              <p className="qd-contract-desc">
                Your air freight contract is now available. Read through the
                details and sign to proceed with your vendor contract.
              </p>
              <button
                className="qd-btn qd-btn-primary"
                onClick={() => navigate(RoutesPaths.FREIGHTAGREEMENT)}
              >
                View contract
              </button>
            </div>
            <img src="/assets/img/contract.png" alt="" className="qd-contract-img" />
          </div>
        )}

      {/* KPI cards */}
      <div className="qd-stats-grid">
        <StatCard
          icon={<FiBox />}
          tone="orange"
          label="Total bookings"
          value={dashboardData?.totalBookings}
          loading={loadingDashboard}
          trend={getTrend(yearSeries.totalBookings)}
          caption="All time"
          spark={{ data: bookingsSpark, color: "#F04927" }}
          onClick={() => navigate(RoutesPaths.BOOKINGMANAGEMENT)}
        />
        {isAdmin ? (
          <StatCard
            icon={<FiUser />}
            tone="indigo"
            label="Total users"
            value={dashboardData?.totalUsers}
            loading={loadingDashboard}
            caption="Registered customers"
            onClick={() => navigate(RoutesPaths.USERMANAGEMENT)}
          />
        ) : (
          <StatCard
            icon={<FiCheckCircle />}
            tone="indigo"
            label="Completed bookings"
            value={completedBookings}
            loading={loadingDashboard}
            trend={getTrend(yearSeries.deliveredBookings)}
            caption="Delivered to date"
            spark={{ data: sparkWindow(yearSeries.deliveredBookings), color: "#6366F1" }}
            onClick={() => navigate(RoutesPaths.BOOKINGMANAGEMENT)}
          />
        )}
        <StatCard
          icon={<FiTruck />}
          tone="green"
          label="Active shipments"
          value={activeShipments}
          loading={loadingDashboard}
          trend={getTrend(activeSeries)}
          caption="In progress & in transit"
          spark={{ data: sparkWindow(activeSeries), color: "#22A55B" }}
          onClick={() => navigate(RoutesPaths.BOOKINGMANAGEMENT)}
        />
        {isAdmin && (
          <StatCard
            icon={<FiFileText />}
            tone="rose"
            label="Newsletter subscribers"
            value={subscriberCount}
            loading={loadingDashboard}
            caption="Active subscribers"
            onClick={() => navigate(RoutesPaths.SUBSCRIBERMANAGEMENT)}
          />
        )}
      </div>

      {/* Charts */}
      <div className="qd-charts-grid">
        <div className="qd-card qd-panel">
          <div className="qd-panel-header">
            <div className="qd-panel-title">
              <span className="qd-panel-icon">
                <FiBarChart2 />
              </span>
              <h6>Booking stats</h6>
            </div>
            <select
              className="qd-select"
              value={statsRange}
              onChange={(e) => setStatsRange(Number(e.target.value))}
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="qd-bar-wrap">
            {loadingDashboard ? (
              <div className="qd-skel" style={{ width: "100%", height: "100%" }} />
            ) : (
              <canvas ref={barCanvasRef} />
            )}
          </div>
        </div>

        <div className="qd-card qd-panel">
          <div className="qd-panel-header">
            <div className="qd-panel-title">
              <span className="qd-panel-icon">
                <FiPieChart />
              </span>
              <h6>Total types</h6>
            </div>
          </div>
          <div className="qd-types">
            <div className="qd-donut">
              {!loadingDashboard && <canvas ref={donutCanvasRef} />}
              <div className="qd-donut-center">
                <strong>{logisticsTotal}</strong>
                <span>Total types</span>
              </div>
            </div>
            <div className="qd-type-list">
              {loadingDashboard
                ? [...Array(5)].map((_, i) => (
                    <div className="qd-type-row" key={i}>
                      <div className="qd-skel" style={{ width: "100%", height: 14 }} />
                    </div>
                  ))
                : logistics.map((item) => {
                    const pct = logisticsTotal
                      ? Math.round((item.count / logisticsTotal) * 100)
                      : 0;
                    return (
                      <div className="qd-type-row" key={item.logisticsType}>
                        <span className="qd-type-name">
                          <span
                            className="qd-dot"
                            style={{ backgroundColor: typeColor(item) }}
                          />
                          {formatLogisticsType(item.logisticsType)}
                        </span>
                        <span className="qd-type-count">
                          {item.count} {item.count === 1 ? "type" : "types"}
                        </span>
                        <span className="qd-type-pct">{pct}%</span>
                      </div>
                    );
                  })}
            </div>
          </div>
        </div>
      </div>

      {/* Customer reviews */}
      {isAdmin && !loadingDashboard && review && (
        <div className="qd-card qd-panel qd-reviews">
          <div className="qd-panel-header">
            <div className="qd-panel-title">
              <h6>Customer reviews</h6>
              <span className="qd-muted">
                {currentReviewIndex + 1} of {reviews.length}
              </span>
            </div>
            <div className="qd-review-nav">
              <button
                className="qd-icon-btn"
                disabled={currentReviewIndex === 0}
                onClick={() => setCurrentReviewIndex((p) => Math.max(0, p - 1))}
                aria-label="Previous review"
              >
                <FiChevronLeft />
              </button>
              <button
                className="qd-icon-btn"
                disabled={currentReviewIndex >= reviews.length - 1}
                onClick={() =>
                  setCurrentReviewIndex((p) => Math.min(reviews.length - 1, p + 1))
                }
                aria-label="Next review"
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
          <div className="qd-review-body">
            <img
              src={
                review?.user?.profileImage
                  ? `${process.env.REACT_APP_IMAGE_URL}/${review.user.profileImage}`
                  : "/assets/img/default-profile.svg"
              }
              alt=""
              className="qd-review-avatar"
            />
            <div>
              <div className="qd-review-head">
                <h6>{review?.user?.fullName || "Unknown"}</h6>
                <span className="qd-review-stars">
                  {[...Array(5)].map((_, i) => (
                    <img
                      key={i}
                      src={
                        i < review.rating
                          ? "/assets/img/gold-star.svg"
                          : "/assets/img/grey-star.svg"
                      }
                      alt=""
                    />
                  ))}
                </span>
                <span className="qd-muted">
                  {new Date(review?.createdAt).toLocaleString()}
                </span>
              </div>
              <p className="qd-review-text">{review.comment}</p>
            </div>
          </div>
        </div>
      )}

      {/* Bookings */}
      {loadingBookings ? (
        <div className="qd-card qd-panel">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="qd-skel" style={{ height: 52, marginBottom: 10 }} />
          ))}
        </div>
      ) : (
        <BookingDetails data={bookings} fetchBookings={fetchBookings} />
      )}
    </div>
  );
}
